import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { connect, Dispatch } from 'react-redux';
import { IApplicationState, AppActionCreators, IRccapState, RccapActionCreators, IRccapLookupDataResponse, IRccapResponse, ILookupData } from '@nkr/store/index';
import { IRccap, IBaseResponse, IRccapCenterResponseAdditionalQuestionAnswer } from '@nkr/models/index';
import { Row, Col, FormControl, Panel, Glyphicon, Button, Alert, FormGroup, PanelGroup, ControlLabel, HelpBlock } from 'react-bootstrap';
import { toast } from 'react-toastify';

interface IAdditionalQuestionWithValidation {
    id: number,
    question: string,
    answer: string,
    isValid: boolean
}

interface IProps {
    id: number,
    centerId: number
}

interface IStateProps {
    rccap: IRccapState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getLookupData: () => Promise<IRccapLookupDataResponse>,
    setLookupData: (lookupData: ILookupData) => void,
    getRccap: (id: number) => Promise<IRccapResponse>,
    setRccap: (rccap: IRccap) => void,
    saveRccapCenterResponse: (issueDescription: string, questions: IRccapCenterResponseAdditionalQuestionAnswer[], rootCausePrimary: number, rootCauseSecondary: number, correctiveAction: string, responseDate: string, responder: string) => Promise<IBaseResponse>
}

type TProps =
    IProps
    & IStateProps
    & IDispatchProps;

interface IValidation {
    issueDescription: boolean,
    correctiveAction: boolean,
    responder: boolean
}

interface IState {
    issueDescription: string,
    additionalQuestions: IAdditionalQuestionWithValidation[],
    rootCausePrimary: number,
    rootCauseSecondary: number,
    correctiveAction: string,
    responder: string,
    responseDate: string,
    validation: IValidation,
    allowSave: boolean,
    dirty: boolean
}

class RccapResponse extends React.Component<TProps, IState> {
    readonly state: IState = {
        issueDescription: '',
        additionalQuestions: [],
        rootCausePrimary: 0,
        rootCauseSecondary: 0,
        correctiveAction: '',
        responder: '',
        responseDate: moment().format('MM/DD/YYYY'),
        validation: {
            issueDescription: true,
            correctiveAction: true,
            responder: true
        },
        allowSave: true,
        dirty: false
    }

    componentDidMount = () => {
        this.getData();
    }

    handleIssueDescriptionChange = (e: React.FormEvent<FormControl & HTMLTextAreaElement>) => {
        let issueDescription = e.currentTarget.value;
        let validation = { ...this.state.validation };

        if (this.state.dirty)
            validation.issueDescription = issueDescription != '';

        this.setState({
            issueDescription: e.currentTarget.value,
            validation
        });
    }

    handleAnswerChange = (answer: string, index: number) => {
        let additionalQuestions = _.cloneDeep(this.state.additionalQuestions);

        additionalQuestions[index].answer = answer;

        if (this.state.dirty)
            additionalQuestions[index].isValid = answer != '';

        this.setState({
            additionalQuestions
        });
    }

    handleRootCausePrimaryChange = (e: React.FormEvent<HTMLSelectElement & FormControl>) => {
        let rootCausePrimary = parseInt(e.currentTarget.value);

        let rootCause = this.props.rccap.lookupData.causes.find(x=>x.id == rootCausePrimary);
        let rootCauseSecondary = rootCause.secondaryCauses.length > 0 ? rootCause.secondaryCauses[0].id : -1;

        this.setState({
            rootCausePrimary,
            rootCauseSecondary
        });
    }

    handleRootCauseSecondaryChange = (e: React.FormEvent<HTMLSelectElement & FormControl>) => {
        this.setState({
            rootCauseSecondary: parseInt(e.currentTarget.value)
        });
    }

    handleCorrectiveActionChange = (e: React.FormEvent<HTMLTextAreaElement & FormControl>) => {
        let correctiveAction = e.currentTarget.value;
        let validation = { ...this.state.validation };

        if (this.state.dirty)
            validation.correctiveAction = correctiveAction != '';

        this.setState({
            correctiveAction,
            validation
        });
    }

    handleResponderChange = (e: React.FormEvent<HTMLInputElement & FormControl>) => {
        let validation = { ...this.state.validation };

        if (this.state.dirty)
            validation.responder = e.currentTarget.value != '';

        this.setState({
            responder: e.currentTarget.value,
            validation
        });
    }

    handleSubmit = () => {
        let validation = this.validate();
        let additionalQuestions = this.validateQuestions();
        let isValid = additionalQuestions.filter(answer => answer.isValid == false).length == 0 && validation.issueDescription && validation.correctiveAction && validation.responder;

        this.setState({
            additionalQuestions,
            validation,
            dirty: true
        }, () => {
            if (isValid) {
                let { issueDescription, additionalQuestions: additionalQuestionsFromState, rootCausePrimary, rootCauseSecondary, correctiveAction, responseDate, responder } = this.state

                let questions: IRccapCenterResponseAdditionalQuestionAnswer[] = additionalQuestionsFromState.map(item => {
                    let result: IRccapCenterResponseAdditionalQuestionAnswer = {
                        id: item.id,
                        answer: item.answer
                    };

                    return result;
                });

                this.props.showLoading('Saving response, one moment please...').then(() => {
                    this.props.saveRccapCenterResponse(issueDescription, questions, rootCausePrimary, rootCauseSecondary, correctiveAction, responseDate, responder).then(response => {
                        this.props.hideLoading();

                        if (response.data.success) {
                            toast.success('RCCAP response submitted successfully...', {
                                onClose: () => {
                                    window.location.href = `/center/${this.props.centerId}/action-center/rccap`
                                }
                            });
                        } else {
                            toast.error('RCCAP response not submitted successfully.');
                        }
                    })
                });
            }
        });
    }

    handleCancel = () => {
        window.location.href = `/center/${this.props.centerId}/reports/my-center/rccap`;
    }

    validate = () => {
        let { issueDescription, correctiveAction, responder } = this.state;

        let validation = {
            issueDescription: issueDescription != '',
            correctiveAction: correctiveAction != '',
            responder: responder != ''
        };

        return validation;
    }

    validateQuestions = () => {
        let additionalQuestions = _.cloneDeep(this.state.additionalQuestions);

        additionalQuestions = additionalQuestions.map(item => {
            item.isValid = item.answer != null && item.answer != '';
            return item;
        });

        return additionalQuestions;
    }

    getData = () => {
        this.props.showLoading('Getting rccap info, one moment please...').then(() => {
            Promise.all([
                this.props.getRccap(this.props.id),
                this.props.getLookupData()
            ])
                .then((response: [IRccapResponse, IRccapLookupDataResponse]) => {
                    this.props.hideLoading();

                    let rccapResponse = response[0];
                    let rccap = rccapResponse.data.results;
                    let rccapLookupDataResponse = response[1];
                    let lookupData = rccapLookupDataResponse.data.results;

                    let additionalQuestions = rccap.additionalQuestions.map(item => {
                        let newItem: IAdditionalQuestionWithValidation = {
                            id: item.id,
                            question: item.question,
                            answer: item.answer,
                            isValid: true
                        };

                        return newItem;
                    });

                    this.setState({
                        issueDescription: rccap.whatHappened,
                        additionalQuestions,
                        rootCausePrimary: rccap.rootCausePrimaryId,
                        rootCauseSecondary: rccap.rootCauseSecondaryId,
                        correctiveAction: rccap.correctiveActionInfo
                    }, () => {
                        this.props.setRccap(rccap);
                        this.props.setLookupData(lookupData);
                    });
                });
        });
    }

    render = () => {
        if (this.props.rccap.info == null)
            return null;

        const primaryCauseList = this.props.rccap.lookupData.causes;
        const defaultSecondaryCauses = primaryCauseList.length > 0 ? primaryCauseList[0].secondaryCauses : []
        const secondaryCauseList = this.state.rootCausePrimary > 0 ? primaryCauseList.find(c => c.id == this.state.rootCausePrimary).secondaryCauses : defaultSecondaryCauses;

        const {
            swapChainNumber, swapClusterNumber, swapPositionNumber, donorCenterAlias, donorAlias,
            recipientCenterAlias, recipientAlias, rccapManager, failDate, dueDate, recipientsDonorAbo, recipientAbo
        } = this.props.rccap.info;

        const { additionalQuestions } = this.state;
        const { issueDescription, correctiveAction, responder } = this.state.validation;
        const isValid = additionalQuestions.filter(x => x.isValid == false).length == 0 && issueDescription && correctiveAction && responder;

        return (
            <>
                <div className='container' style={{ marginTop: 20, marginBottom: 20 }}>
                    {this.state.dirty && !isValid && (
                        <div className='row'>
                            <div className='col-xs-12 col-lg-12'>
                                <Alert bsStyle='danger'>
                                    We are unable to submit your response until the below items are completed
                            </Alert>
                            </div>
                        </div>
                    )}

                    <div className='row'>
                        <div className='col-xs-12 col-lg-12'>
                            <div className='alert alert-info' style={{ padding: 7, marginBottom: 20, display: 'flex', justifyContent: 'space-around' }}>
                                <div className='text-center'>
                                    <span style={{ fontWeight: 700 }}>Swap #</span><br />
                                    <span>{swapChainNumber}/{swapClusterNumber}/{swapPositionNumber}</span>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div className='text-center'>
                                        <span style={{ fontWeight: 700 }}>{donorCenterAlias}</span>
                                        <br />
                                        <span>
                                            Alias:&nbsp;{donorAlias}&nbsp;/&nbsp;ABO:&nbsp;{recipientsDonorAbo}
                                        </span>
                                    </div>

                                    <div className='text-center' style={{ width: 50 }}>
                                        <Glyphicon glyph='forward' />
                                    </div>

                                    <div className='text-center'>
                                        <span style={{ fontWeight: 700 }}>{recipientCenterAlias}</span><br />
                                        <span>Alias:&nbsp;{recipientAlias}&nbsp;/&nbsp;ABO:&nbsp;{recipientAbo}</span>
                                    </div>
                                </div>

                                <div className='text-center'>
                                    <span style={{ fontWeight: 700 }}>Swap Manager</span><br />
                                    <span>{rccapManager}</span>
                                </div>
                                <div className='text-center'>
                                    <span style={{ fontWeight: 700 }}>Date Failed</span><br />
                                    <span>{failDate}</span>
                                </div>
                                <div className='text-center'>
                                    <span style={{ fontWeight: 700 }}>Date Due</span><br />
                                    <span>{dueDate}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='row'>
                        <div className='col-xs-12 col-lg-12'>
                            <FormGroup validationState={this.state.validation.issueDescription ? null : 'error'}>
                                <ControlLabel>Problem Description</ControlLabel>
                                <FormControl
                                    componentClass='textarea'
                                    style={{ resize: 'none', height: 75 }}
                                    value={this.state.issueDescription}
                                    onChange={this.handleIssueDescriptionChange}
                                />

                                {!this.state.validation.issueDescription && (
                                    <HelpBlock>This is required...</HelpBlock>
                                )}
                            </FormGroup>


                            {this.state.additionalQuestions.map((item, index) =>
                                <FormGroup validationState={this.state.additionalQuestions[index].isValid ? null : 'error'}>
                                    <ControlLabel>{item.question}</ControlLabel>
                                    <FormControl
                                        componentClass='textarea'
                                        style={{ resize: 'none' }}
                                        value={this.state.additionalQuestions[index].answer}
                                        onChange={(e: React.FormEvent<HTMLTextAreaElement & FormControl>) => this.handleAnswerChange(e.currentTarget.value, index)}
                                    />

                                    {this.state.additionalQuestions[index].isValid == false && (
                                        <HelpBlock>Please answer this question...</HelpBlock>
                                    )}
                                </FormGroup>
                            )}

                            <FormGroup>
                                <ControlLabel>Primary Root Cause</ControlLabel>
                                <FormControl
                                    componentClass='select'
                                    value={this.state.rootCausePrimary}
                                    onChange={this.handleRootCausePrimaryChange}
                                >
                                    {primaryCauseList.map((cause, index) =>
                                        <option key={index} value={cause.id}>{cause.description}</option>
                                    )}
                                </FormControl>
                            </FormGroup>

                            <FormGroup>
                                <ControlLabel>Secondary Root Cause</ControlLabel>
                                <FormControl
                                    componentClass='select'
                                    value={this.state.rootCauseSecondary}
                                    onChange={this.handleRootCauseSecondaryChange}
                                >
                                    {secondaryCauseList.map((cause, index) =>
                                        <option key={index} value={cause.id}>{cause.description}</option>
                                    )}
                                </FormControl>
                            </FormGroup>

                            <FormGroup validationState={this.state.validation.correctiveAction ? null : 'error'}>
                                <ControlLabel>Corrective Action</ControlLabel>
                                <FormControl
                                    componentClass='textarea'
                                    style={{ resize: 'none', height: 75 }}
                                    value={this.state.correctiveAction}
                                    onChange={this.handleCorrectiveActionChange}
                                />
                            </FormGroup>

                            <Row>
                                <Col xs={12} lg={3}>
                                    <FormGroup>
                                        <ControlLabel>Response Date</ControlLabel>
                                        <FormControl
                                            disabled
                                            value={this.state.responseDate}
                                        />
                                    </FormGroup>
                                </Col>

                                <Col xs={12} lg={2}>
                                    <FormGroup validationState={this.state.validation.responder ? null : 'error'}>
                                        <ControlLabel>Your Initials</ControlLabel>
                                        <FormControl
                                            value={this.state.responder}
                                            onChange={this.handleResponderChange}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Row>
                        <Col xs={12} lg={12} style={{ textAlign: 'center' }}>
                            <Button bsStyle='primary' bsSize='large' style={{ marginRight: 5 }} disabled={this.state.allowSave == false} onClick={this.handleSubmit}>Submit Response</Button>
                            <Button bsStyle='link' bsSize='medium' onClick={this.handleCancel}>Cancel &amp; Return to the RCCAP List</Button>
                        </Col>
                    </Row>
                </div>
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        rccap: appState.rccap
    };
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => { return dispatch(AppActionCreators.showLoadingAsync(message)); },
        hideLoading: () => { dispatch(AppActionCreators.hideLoading()); },
        getLookupData: () => { return dispatch(RccapActionCreators.getRccapLookupData(false)); },
        setLookupData: (lookupData) => { dispatch(RccapActionCreators.setRccapLookupData(lookupData)); },
        getRccap: (id) => { return dispatch(RccapActionCreators.getRccap(id, false)); },
        setRccap: (rccap: IRccap) => { dispatch(RccapActionCreators.setRccap(rccap)); },
        saveRccapCenterResponse: (issueDescription, questions, rootCausePrimary, rootCauseSecondary, correctiveAction, responseDate, responder) => { return dispatch(RccapActionCreators.saveRccapCenterResponse(issueDescription, questions, rootCausePrimary, rootCauseSecondary, correctiveAction, responseDate, responder)); }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(RccapResponse)